import { translationPacket } from 'common/actions';
import { Column, Row } from 'common/components/common/style';
import {
  DescriptionStaff,
  DescriptionSubtitle,
  DescriptionTitle
} from 'common/components/description';
import MetadataSeparator from 'common/components/metadata-strip/MetadataSeparator';
import MetadataStrip from 'common/components/metadata-strip/MetadataStrip';
import { H3, P1 } from 'common/components/Typography';
import variables from 'common/config/variables';
import { Epg, LiveChannels } from 'common/constants/data-types';
import { INextVideo, PlayerControlsState } from 'common/reducers/videoPlayer';
import { getDateAndTimeFast, getDayOfWeek } from 'common/services/helpers';
import { getClassName, getScalablePixel } from 'common/utils';
import { usePrevious } from 'common/utils/hooks/usePrevious';
import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { DescriptionTitlesContainer } from './Controls.styles';
const { font_color_c } = variables;

interface DetailedInfoProps {
  // Redux injected
  i18n: typeof translationPacket;
  playerControlsState: PlayerControlsState;
  currentEpg?: Epg;
  liveChannels?: LiveChannels;
  vod?: boolean;
  vodAsset?: Epg;
  //
  bottomViewVisible?: boolean;
  vodSelectionPage?: boolean;
}
function DetailedInfo(props: DetailedInfoProps) {
  const previousPlayerControls = usePrevious(props.playerControlsState);
  const descriptionContainerVisible = useMemo(
    () =>
      (['expanded', 'hidden'].includes(props.playerControlsState) &&
        previousPlayerControls !== 'mini') ||
      props.playerControlsState === 'expanded',
    [props.playerControlsState, previousPlayerControls]
  );
  const dateTime = useMemo(() => {
    try {
      if (!props.currentEpg?.start || !props.currentEpg?.stop || !props.i18n) {
        return undefined;
      }
      const { start, stop } = props.currentEpg;
      return getDateAndTimeFast(start, stop, props.i18n.months);
    } catch (error) {
      return undefined;
    }
  }, [props.currentEpg, props.i18n]);

  const dayOfWeek = useMemo(() => {
    if (!props.currentEpg || !props.currentEpg?.start || !props.i18n) {
      return '-';
    }
    return getDayOfWeek(props.currentEpg.start, props.i18n.days).toUpperCase();
  }, [props.currentEpg]);

  if (isEmpty(props.currentEpg) || isEmpty(props.liveChannels)) {
    return null;
  }

  return (
    <Column style={{ willChange: 'transform' }}>
      <Row justifyContent="flex-start" style={{ padding: `${getScalablePixel(16)}px 0` }}>
        {!props.vod && (
          <Column paddingRight={getScalablePixel(30)}>
            <Row justifyContent="flex-start">
              {<P1 style={{ color: font_color_c }}>{dayOfWeek}</P1>}
              <MetadataSeparator padding={8} size={getScalablePixel(32)} />
              {dateTime && (
                <H3 style={{ color: font_color_c }}>{`${
                  dateTime.day
                } ${dateTime.month.toUpperCase()}`}</H3>
              )}
            </Row>
            {dateTime && (
              <DescriptionTitle
                fontWeightNormal
                testId="player-detailed-info-datetime"
              >{`${dateTime.start} - ${dateTime.stop}`}</DescriptionTitle>
            )}
          </Column>
        )}
        <DescriptionTitlesContainer>
          <DescriptionSubtitle
            numeration={props.currentEpg.numeration}
            originaltitle={props.currentEpg.originaltitle}
            testId="player-detailed-info-subtitle"
          />
          <DescriptionTitle
            lines={props.vodSelectionPage ? 2 : 1}
            large={props.vodSelectionPage}
            testId="player-detailed-info-title"
          >
            {props.currentEpg.title}
          </DescriptionTitle>
        </DescriptionTitlesContainer>
      </Row>
      <MetadataStrip
        year={props.currentEpg.year || props.vodAsset?.releaseDate?.split('-')[0]}
        country={props.currentEpg.country}
        major_category={props.currentEpg.major_category}
        minor_categories={props.currentEpg.minor_categories}
        duration={props.currentEpg.duration || props.currentEpg.epgs?.[0]?.duration}
        rating={props.currentEpg.rating}
        resolution={
          props.liveChannels[props.currentEpg.chan_id]?.resolution ||
          props.currentEpg.profiles?.[0]?.resolution ||
          props.currentEpg.epgs?.[0]?.profiles?.[0]?.resolution
        }
      />
      <div
        className={getClassName('detailed-info-description-container', {
          visible: !props.vod && descriptionContainerVisible && !props.bottomViewVisible
        })}
      >
        <span className="description" data-test-id="detailed-info-description">
          {props.currentEpg.shorttext || props.currentEpg.fulltext || props.currentEpg.subtitle}
        </span>
        <DescriptionStaff actors={props.currentEpg.actors} persons={props.currentEpg.persons} />
      </div>
    </Column>
  );
}

const mapStateToProps = ({ i18n, videoPlayer, liveChannels }: any) => {
  const vodPreview =
    videoPlayer.video?.vod && !isEmpty(videoPlayer.video.vodAsset) && !videoPlayer.video?.source;

  const nextVideo: INextVideo | undefined = isEmpty(videoPlayer.nextVideo)
    ? undefined
    : videoPlayer.nextVideo;
  const nextEpg =
    nextVideo && !isEmpty(liveChannels) && liveChannels[nextVideo.channelId].currentepg;
  const epg = isEmpty(videoPlayer.epgs?.current) ? undefined : videoPlayer.epgs.current;
  const currentEpg = vodPreview ? videoPlayer.video.vodAsset : epg;

  return {
    i18n,
    currentEpg: nextEpg || currentEpg,
    liveChannels,
    playerControlsState: videoPlayer.playerControlsState,
    vod: videoPlayer.video?.vod,
    vodAsset: videoPlayer.video?.vodAsset
  };
};

export default React.memo(connect(mapStateToProps, {})(DetailedInfo));
